import { useLayoutEffect, useRef, type ReactNode } from "react";
import { Redirect, useLocation } from "wouter";
import { TopBar } from "@/components/navigation/TopBar";
import { FullPageLoader } from "@/components/shell/FullPageLoader";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { ROUTES, landingInstance } from "@/lib/routes";
import { OverviewNoInstance } from "./OverviewNoInstance";

/**
 * The admin landing (Session 10, B1; decisions "Admin landing confirmed").
 *
 * `App.tsx` renders this for a signed-in user on `/` or `/overview` with NO instance selected.
 * Before Session 10 that user went to the picker; a PlatformAdmin or TenantAdmin reaches several
 * instances and so always stopped there. Now they go into the first instance they can read
 * (`landingInstance`: the picker's own list, by tenant name then instance name) and continue to
 * `/overview`. With none, Overview says "No instance yet". Only `/` and `/overview` apply this
 * rule; the picker and every other route are unchanged. Since round 6 (T-08) this renders only
 * when the person's saved instance is not reachable either (`TenantInstanceContext` lands on that
 * one first), and the instance chosen here is then saved in its place.
 */
export function OverviewLanding({ idleDialog }: { idleDialog: ReactNode }) {
  const { instances } = useTenantInstance();
  const [location] = useLocation();
  const target = landingInstance(instances);

  if (target) return <LandOnInstance instanceId={target.id} />;

  // Instances exist but none is active and grants `instance.read` (#419: one may still be
  // provisioning): "nothing has been shared with you" would be false, so the picker (the
  // pre-Session-10 behaviour) lists them, says what each allows and badges one being set up.
  if (instances.length > 0) return <Redirect to="/instances" replace />;

  // No instance at all: the empty state lives at `/overview`, so `/` says where it is.
  if (location !== ROUTES.overview) return <Redirect to={ROUTES.overview} replace />;

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <div className="pt-12">
        <OverviewNoInstance />
      </div>
      {idleDialog}
    </div>
  );
}

/**
 * Selects the instance and continues to its Overview (`/i/<id>/overview`, round 9 T-58: the
 * switch itself navigates there, replacing this address).
 *
 * Written in a layout effect, once, behind a ref that survives React's double-invoked mount in
 * development: selecting writes to storage, and a write in a render body would also run on
 * renders React throws away. `selectInstance` is the picker's own call, so every later request
 * carries exactly the header a click in the picker would have set. The first frame shows the
 * loader the shell already shows while access loads.
 */
function LandOnInstance({ instanceId }: { instanceId: string }) {
  const { selectInstance } = useTenantInstance();
  const done = useRef(false);

  useLayoutEffect(() => {
    if (done.current) return;
    done.current = true;
    // Saved as the person's last instance once selected (`TenantInstanceContext`, round 6 T-08):
    // the landing runs only when no saved instance is reachable, so this replaces a gone one.
    selectInstance(instanceId, { replace: true });
  }, [instanceId, selectInstance]);

  return <FullPageLoader message="Opening your instance…" />;
}
